import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles  } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TableCell from '@mui/material/TableCell';
import { lighten  } from '@mui/material/styles';
import GridHead from './GridHead';
import TableToolbar from './TableToolbar';
import DataRow from './DataRow';
import GridTotals from './GridTotals';
import { getSorting } from './utils/sorting';
import {
  density,
  getRowHeight,
  getCheckboxWidth,
  getBadgeColumnWidth,
  getMinColumnWidth
} from './utils/density';
import {
  getGridSetting,
  setGridSetting,
  restore
} from './utils/storage';
import FilterCriteria from './FilterCriteria';

// when the version number is incremented, the saved grids for all users will be reset
// If you forget this, grid changes will not show up
const VERSION = 36;

const styles = theme => ({
  root: {
    width: '100%'
  },
  tableWrapper: {
    overflow: 'scroll',
    paddingLeft: 14,
    paddingBottom: 32
  },
  table: {
    tableLayout: 'fixed'
  },
  row: {
  },
  selected: {
    background: lighten(theme.palette.primary.light, 0.85)
  },
  flexZero: {
    flex: 0
  },
  ...density
});


class DataGrid extends Component {
  constructor(props) {
    super(props);
    this.checkVersion();
    const gridSettings = getGridSetting(props.title);
    const columns = this.restoreColumnSettings(props);
    const filterCriteria = new FilterCriteria(props.title, columns);
    filterCriteria.load();
    filterCriteria.observer = this.onApplyFilter.bind(this);
    let sorting = getGridSetting(props.title, 'sorting');
    if (!sorting) {
      const sort = props.sortDirection ? props.sortDirection : 'asc';
      const sortBy = props.sortColumn ? props.sortColumn : 'name';
      sorting = { sort, sortBy };
    }

    const settings = {
      autosaveWidth: !((gridSettings && gridSettings.autosaveWidth === false)),
      autosaveOrder: !((gridSettings && gridSettings.autosaveOrder === false)),
      density: (gridSettings && gridSettings.density) ? gridSettings.density : 'default'
    };

    this.tableRef = React.createRef();
    this.state = {
      ...sorting,
      dataRows: DataGrid.dataRowsFromData(props.data),
      filterCriteria,
      columns,
      page: 0,
      count: 0,
      oneSelected: null,
      rowsPerPage: getGridSetting(props.title, 'rowsPerPage') || props.rowsPerPage || 25,
      innerHeight: window.innerHeight,
      selectedRows: [],
      numSelected: 0,
      settings
    };
  }

  componentDidMount() {
    const { onLoad } = this.props;
    const { columns, settings } = this.state;
    const { tableWidth } = this.state;
    const currentWidth = this.tableRef.current ? this.tableRef.current.getBoundingClientRect().width : 0;

    document.addEventListener('keydown', this.handleShortcuts);
    window.addEventListener('resize', this.handleResize);

    if (tableWidth !== currentWidth) {
      this.setState({
        tableWidth,
        columns: this.initColumnProperties(this.customizeColumns(columns), settings, currentWidth)
      })
    }

    if (typeof onLoad === 'function') {
      onLoad(this.getData().map(r => r.data));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.data !== prevProps.data) {
      this.setState({
        dataRows: DataGrid.dataRowsFromData(this.props.data, this.state.selectedRows)
      });
    }
  }

  onApplyFilter() {
    const { filterCriteria } = this.state;
    filterCriteria.save();
    this.setState({}) // force repaint
  }

  handleShortcuts = (evt) => {
    if (evt.altKey && evt.key === 'PageDown') this.skipPage(1);
    if (evt.altKey && evt.key === 'PageUp') this.skipPage(-1);
  };

  handleResize = (evt) => {
    this.setState({ innerHeight: window.innerHeight });
  };

  skipPage = (offset) => {
    const { pagination } = this.props;
    if (pagination) {
      const { page } = this.state;
      let newPage = page + offset;
      newPage = Math.max(newPage, 0);
      if (newPage !== page) {
        this.setState({ page: newPage });
      }
    }
  };

  /*
   * restore() deletes the grid settings in local storage
   */
  checkVersion = () => {
    const settings = getGridSetting('jam-grid-global');
    if (!settings || settings.version !== VERSION) {
      console.log('delete local grid settings');
      restore();
      setGridSetting('jam-grid-global', 'version', VERSION);
    }
  };

  /** ******************************************
   *          Setup Handlers
   ****************************************** */

  initColumnProperties = (columns, settings, tableWidth) => {
    columns = this.initColumnVisibility(columns);
    columns = this.initColumnWidths(columns, settings, tableWidth);
    return columns;
  }

  initColumnVisibility = columns => {
    return columns.map(col => {
      if (col.visible === undefined || col.visible !== false) {
        col.visible = true;
      }
      return col;
    });
  }

  initColumnWidths = (columns, settings, tableWidth) => {
    const { selectable } = this.props;
    const cols = columns.map((col, ix) => {
      if (col.visible && !col.width) {
        const { density } = settings;
        if (col.badge) {
          col.width = getBadgeColumnWidth(density);
        } else {
          let width = getMinColumnWidth(density);
          const offset = selectable ? getCheckboxWidth(density).width : 0;
          const absoluteMinWidth = (tableWidth - offset) / columns.length;
          const combinedWidth = columns.reduce((a, v) => a + (v.width || width), 0);
          if (tableWidth > combinedWidth) {
            width = absoluteMinWidth;
          }
          col.width = Math.max(width, col.width || 0);
        }
      }
      return col;
    });
    setGridSetting(this.props.title, 'columns', cols);
    return cols;
  }

  resetColumnWidths = () => {
    const { columns, tableWidth, settings } = this.state;
    const cols = columns.map(col => {
      delete col.width;
      return col;
    });
    return this.initColumnWidths(cols, settings, tableWidth)
  }

  /** ******************************************
   *          Sort Handlers
   ****************************************** */

  saveSort = (sort, sortBy) => {
    const { title, onSort } = this.props;
    setGridSetting(title, 'sorting', { sortBy, sort });

    if (typeof onSort === 'function') {
      onSort(this.getData());
      onSort(this.getData().map(r => r.data));
    }
  };

  handleRequestSort = (event, property) => {
    const sortBy = property;
    let sort = 'desc';

    if (this.state.sortBy === property && this.state.sort === 'desc') {
      sort = 'asc';
    }

    this.setState({ sort, sortBy });
    this.saveSort(sort, sortBy);
  };

  /** ******************************************
   *          Select Handlers
   ****************************************** */

  handleSelectAllClick = (event, checked) => {
    const filteredData = this.filterData();
    filteredData.forEach(r => {
      r.selected = checked;
      if (r.ref && r.ref.current) {
        r.ref.current.repaint();
      }
    });
    this.propagateSelectedRows();
  };

  /**
   * We want all the ids between id1 and id2, which could occur in either order
   * @param id1 one of the selected ids
   * @param id2 the other selected id
   */
  handleMultiSelect = (id1, id2) => {
    const filteredData = this.filterAllBetweenTwo(id1, id2, this.filterData());
    filteredData.forEach(r => {
      r.selected = true;
      if (r.ref && r.ref.current) {
        r.ref.current.repaint();
      }
    });
    this.propagateSelectedRows();
  };

  filterAllBetweenTwo = (id1, id2, data) => {
    let selecting = false;
    const selectedData = [];
    let otherId;
    data.forEach(fd => {
      if (!selecting && (fd.data.id === id1 || fd.data.id === id2)) {
        selecting = true;
        otherId = (fd.data.id === id1) ? id2 : id1;
      }
      if (selecting) {
        selectedData.push(fd);
      }
      if (selecting && fd.data.id === otherId) {
        selecting = false;
      }
    });
    return selectedData;
  };

  toggleSelection = (dataRow) => {
    this.propagateSelectedRows();
  };

  propagateSelectedRows = () => {
    const { onSelect } = this.props;
    if (typeof onSelect === 'function') {
      onSelect(this.getSelectedData());
    }
    this.numSelected();
    this.setState({ count: this.state.count + 1 });
  };

  /** ******************************************
   *          Toolbar Handlers
   ****************************************** */

  handleClone = (count) => {
    const { onClone } = this.props;
    if (!onClone) {
      return;
    }
    const selectedData = this.getSelectedData();
    onClone(selectedData, count);
    this.clearSelection();
  }

  handleGetPairLists = () => {
    const { getPairLists } = this.props;
    if (!getPairLists) {
      return {};
    }
    const selectedData = this.getSelectedData();
    return getPairLists(selectedData);
  }

  handlePair = (cap) => {
    const { onPair } = this.props;
    if (!onPair) {
      return;
    }
    const selectedData = this.getSelectedData();
    onPair(selectedData, cap);
    this.clearSelection();
  }

  handleEdit = () => {
    const selectedData = this.getSelectedData();
    this.clearSelection();
    this.props.onEdit(selectedData);
  }

  setIOSelected = () => {
    const selectedData = this.getSelectedData();
    if (this.props.setIOSelected) {
      this.props.setIOSelected(selectedData);
    }
  }

  setPackageSelected = () => {
    const selectedData = this.getSelectedData();
    this.props.setPushData(selectedData, 'packages');
  }

  handleExport = () => {
    this.props.onExport(this.filterData().map(r => r.data));
  }

  handleCancelSelected = () => {
    const selectedData = this.getSelectedData();
    this.clearSelection();
    this.props.onCancel(selectedData);
  }

  handleDeleteSelected = () => {
    const selectedData = this.getSelectedData();
    this.clearSelection();
    this.props.onDelete(selectedData);
  }

  handleUploadSelected = () => {
    const { selected } = this.state;
    const { setIOSelected, setPackageSelected, setPushData } = this.props;
    const sel = this.getSelectedData(selected)
    // console.log('DG handle upload call pushData', sel);
    if (typeof setIOSelected !== 'undefined') {
      setIOSelected(sel);
    }
    if (typeof setPackageSelected !== 'undefined') {
      setPackageSelected(sel);
    }
    if (typeof setPushData !== 'undefined') {
      setPushData(sel, 'placements');
    }
    return sel;
  }

  /** ******************************************
   *          Pagination Handlers
   ****************************************** */

  handleChangePage = (event, page) => {
    this.setState({ page });
  };

  handleChangeRowsPerPage = event => {
    const data = this.getData();
    const { value } = event.target;
    const rowsPerPage = value === 'All' ? data.length : value;
    setGridSetting(this.props.title, 'rowsPerPage', rowsPerPage);
    this.setState({ rowsPerPage: value });
  };

  /** ******************************************
   *          Filter Handlers
   ****************************************** */

  filterData = () => {
    const { filterCriteria } = this.state;
    const { dataRows } = this.state;
    if (filterCriteria.isEmpty) {
      return dataRows;
    }
    return dataRows.filter(row => filterCriteria.matches(row.data));
  }

  /** ******************************************
   *          Settings Handlers
   ****************************************** */

  handleSettingSwitch = (key) => () => {
    const { settings } = this.state;
    const toggled = !settings[key];
    settings[key] = toggled;
    this.setState({ settings });
    setGridSetting(this.props.title, key, toggled);
  }

  handleDensityChange = (evt, density) => {
    const { settings } = this.state;
    if (!density) {
      return;
    }
    this.setState({ settings: {...settings, density }});
    setGridSetting(this.props.title, 'density', density);
  }

  restoreSettings = () => {
    restore();
    window.location.reload();
  }

  /** ******************************************
   *          Data Handlers
   ****************************************** */

  hasTotals = () => {
    if (!this.state.columns) {
      return false;
    }
    return this.state.columns.some(col => col.totals);
  }

  handleColumnChange = columns => {
    this.setState({ columns });
    setGridSetting(this.props.title, 'columns', columns);
  }

  getSelectedData = () => {
    const data = this.getData();
    return data.filter(r => r.selected).map(r => r.data);
  }

  getData = () => {
    const { sortable } = this.props;
    const { sort, sortBy } = this.state;

    let data = this.filterData();
    if (sortable) {
      const sortFn = getSorting(sort, sortBy, true);
      data = data.sort((a, b) => sortFn(a.data, b.data));
    }

    return data;
  }

  paginateData = data => {
    const { pagination } = this.props;
    if (pagination) {
      const { page } = this.state;
      let { rowsPerPage } = this.state;
      if (rowsPerPage === 'All') rowsPerPage = data.length;
      data = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }

    return data;
  }


  /** ******************************************
   *          Render Handlers
   ****************************************** */

  tableRow = (dataRow) => {
    const { columns, settings, oneSelected, selectedRows } = this.state;
    const { selectable, onCellEdit } = this.props;

    dataRow.selected = selectedRows.includes(dataRow.data.id);
    dataRow.ref = React.createRef();
    return (
      <DataRow
        key={`${dataRow.key}`}
        innerRef={dataRow.ref}
        dataRow={dataRow}
        selectable={selectable}
        settings={settings}
        columns={columns}
        onCellEdit={onCellEdit}
        oneSelected={oneSelected}
        onSelect={e => this.toggleSelection(e)}
        handleMultiSelect={this.handleMultiSelect}
      />
    )
  };

  restoreColumnSettings(props) {
    return getGridSetting(props.title, 'columns') || props.columns;
  }

  customizeColumns(columns) {
    const { onCustomizeColumns } = this.props;
    if (!onCustomizeColumns) return columns;

    const colDict = columns.reduce((dic, col) => { dic[col.id] = col; return dic;  }, {});
    onCustomizeColumns (colDict);
    return columns.map(c => colDict[c.id])
  }

  static dataRowsFromData(data, selectedRows = []) {
    if (!data || data.length === 0) {
      return[];
    }
    return data.map((d, idx) => ({
      key: idx,
      data:d,
      selected: selectedRows.includes(d.id),
      ref: React.createRef()
    }));
  }

  clearSelection() {
    const { dataRows } = this.state;
    dataRows.forEach(r => {
      r.selected = false;
      if (r.ref && r.ref.current && r.ref.current.toggleSelection) {
        r.ref.current.toggleSelection(false);
      }
    });
  }

  numSelected() {
    const { dataRows, oneSelected } = this.state;
    const selectedRows = dataRows.filter(r => r.selected).map(r => r.data.id);
    if (selectedRows.length === 1) {
      const selectedOne = dataRows.find(r => r.selected);
      if (selectedOne.data.id !== oneSelected) {
        this.setState({ oneSelected: selectedOne.data.id });
      }
    } else if (oneSelected) {
      this.setState({ oneSelected: null });
    }
    this.setState({ selectedRows:selectedRows, numSelected: selectedRows.length });
    return selectedRows.length;
  }

  clearRowRefs() {
    const { dataRows } = this.state;
    dataRows.forEach(r => { r.ref = undefined; });
  }

  clearFilter() {
    const { filterCriteria } = this.state;
    filterCriteria.clear();
    this.setState({ filterCriteria })
  }

  render() {
    const {
      classes,
      title,
      plain,
      sortable,
      pagination,
      selectable,
      onClone,
      onEdit,
      onAdd,
      onDelete,
      onCancel,
      onExport,
      onSelectedExport,
      suppressCount,
      showFilterRow,
      lineItemCount,
      offsetTop,
      expanded,
      locked,
      canPush,
      onToggleExpand,
      togglePushSettings,
      showSelectedAmount,
      selectedAmount,
      amPushRules,
      isReadOnly,
      handlePushSettings,
      pushPlacementsFromOrder,
      onPushPackages,
      pushPlacementsFromPackage,
      setPushData,
      pushType,
      whitelist
    } = this.props;

    const {
      page,
      sort,
      sortBy,
      columns,
      filterCriteria,
      settings,
      rowsPerPage,
      innerHeight
    } = this.state;

    // console.log('DG render');

    const data = this.getData();
    const currentPage = this.paginateData(data);
    const rowsOptions = this.props.rowsOptions || [10, 25, 50];
    const rowHeight = getRowHeight(settings.density);
    const filterHeight = filterCriteria.isEmpty ? 0 : 70;
    const maxHeight = currentPage.length * rowHeight + (plain ? 125 : 225);
    const tableHeight = plain ? maxHeight : innerHeight - offsetTop - filterHeight;
    const emptyRows = (tableHeight < maxHeight) ? 0 : Math.floor ((tableHeight - maxHeight) / rowHeight);
    this.clearRowRefs();

    return (
      <Paper className={classes.root}>
        <TableToolbar
          title={title}
          plain={plain}
          filterCriteria={filterCriteria}
          columns={columns}
          numSelected={this.state.numSelected}
          rowCount={data.length}
          lineItemCount={lineItemCount || data.length}
          settings={settings}
          onColumnToggle={this.handleColumnChange}
          canAdd={!!onAdd && expanded}
          onAdd={onAdd}
          canClone={!!onClone && expanded}
          getPairLists={this.handleGetPairLists}
          onPair={this.handlePair}
          onClone={this.handleClone}
          canEdit={!!onEdit && expanded}
          onEdit={this.handleEdit}
          canUpload={expanded}
          disabledUpload={locked || canPush}
          onUploadSelected={this.handleUploadSelected}
          canSelectExport={!!onSelectedExport && expanded}
          onSelectExport={this.handleSelectExport}
          canExport={!!onExport && expanded}
          onExport={this.handleExport}
          canDelete={!!onDelete && expanded}
          canCancel={!!onCancel}
          onDelete={this.handleDeleteSelected}
          onCancel={this.handleCancelSelected}
          onSettingSwitch={this.handleSettingSwitch}
          onRestoreSettings={this.restoreSettings}
          onDensityChange={this.handleDensityChange}
          suppressCount={suppressCount}
          summaryDisplay={!expanded}
          onToggleExpand={onToggleExpand}
          showSelectedAmount={showSelectedAmount}
          selectedAmount={selectedAmount}
          amPushRules={amPushRules}
          isReadOnly={isReadOnly}
          togglePushSettings={togglePushSettings}
          pushPlacementsFromOrder={pushPlacementsFromOrder}
          onPushPackages={onPushPackages}
          pushPlacementsFromPackage={pushPlacementsFromPackage}
          setIOSelected={this.setIOSelected}
          setPackageSelected={this.setPackageSelected}
          setPushData={setPushData}
          handlePushSettings={handlePushSettings}
          pushType={pushType}
          whitelist={whitelist}
        />
        {expanded && (
        <div
          className={classes.tableWrapper}
          style={{height: tableHeight}}
          ref={this.tableRef}>
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              className={classes.table}
            >
              <GridHead
                showFilterRow={showFilterRow}
                filterCriteria={filterCriteria}
                numSelected={this.state.numSelected}
                title={title}
                plain={plain}
                columns={columns}
                selectable={selectable}
                sortable={sortable}
                sort={sort}
                sortBy={sortBy}
                settings={settings}
                onSelectAllClick={this.handleSelectAllClick}
                onRequestSort={this.handleRequestSort}
                rowCount={data.length}
                onColumnReorder={this.handleColumnChange}
                onColumnResize={this.handleColumnChange}
              />
              <TableBody>
                {currentPage.map(this.tableRow)}
                {pagination && emptyRows > 0 && (
                  <TableRow style={{height: rowHeight * emptyRows}}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                {this.hasTotals() && (
                <GridTotals
                  columns={columns}
                  data={data}
                  settings={settings}
                />
                )}
                <TableRow>
                  {pagination && (
                    <TablePagination
                      count={data.length}
                      rowsPerPage={rowsPerPage === 'All' ? data.length : rowsPerPage}
                      page={page}
                      backIconButtonProps={{
                        'title': 'Previous Page (Alt+PageUp)'
                      }}
                      nextIconButtonProps={{
                        'title': 'Next Page (Alt+PageDown)'
                      }}
                      classes={{
                        spacer: classes.flexZero
                      }}
                      rowsPerPageOptions={rowsOptions}
                      onPageChange={this.handleChangePage}
                      onRowsPerPageChange={this.handleChangeRowsPerPage}
                    />
                  )}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </div>
        )}
      </Paper>
    )
  }
}

DataGrid.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  title: PropTypes.string.isRequired,
  showFilterRow: PropTypes.bool.isRequired,
  offsetTop: PropTypes.number,
  expanded: PropTypes.bool,
  locked: PropTypes.bool,
  onToggleExpand: PropTypes.func,
  showSelectedAmount: PropTypes.bool,
  selectedAmount: PropTypes.number
};

DataGrid.defaultProps = {
  offsetTop: 240,
  expanded: true,
  locked: false,
  onToggleExpand: () => {}
};

export default withStyles(styles, { withTheme: true })(DataGrid);
